// v642.8: Quick Settings popover — surfaces the four most-toggled
// settings (mute, music vol, font size, compact numbers) in a tiny
// popover anchored to the ⚙️ icon. "More settings…" opens the full
// SettingsPanel for everything else.
// v642.10: added `inline` mode for embedding inside the mobile drawer
// (no absolute positioning, no click-outside dismissal).
// v642.11: desktop variant now portals to <body> and positions itself
// from the trigger's bounding rect, so it can never be painted under
// stacking contexts (game log, chat, etc.) that sit beside the top bar.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useSettings from '../hooks/useSettings';

const QuickSettingsPopover = ({ onClose, onOpenFullSettings, anchorClass = 'dm-quick-settings', inline = false, anchorRef = null }) => {
  const [settings, update] = useSettings();
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // { top, right } when portalled

  // Click-outside + Esc dismiss. Inline mode (mobile drawer) skips this
  // because the parent drawer owns dismissal.
  useEffect(() => {
    if (inline) return undefined;
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      // If the trigger button is what got clicked, also ignore — the
      // parent's onClick toggle will close us. This prevents a
      // double-toggle race where we close, then the parent reopens.
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose && onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    // Defer so the click that opened the popover doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, inline, anchorRef]);

  // Compute position from anchor when portalled. Re-measure on resize so
  // the popover follows a window-resize-driven topbar reflow.
  useLayoutEffect(() => {
    if (inline || !anchorRef?.current) return undefined;
    const measure = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + 6,
        right: Math.max(8, window.innerWidth - r.right),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [inline, anchorRef]);

  const fmt = (v) => Math.round(v * 100);

  const popoverStyle = inline
    ? {
        background: 'rgba(20,24,32,0.6)',
        border: '1px solid rgba(74,106,144,0.5)',
        borderRadius: 6,
        padding: '0.6rem 0.7rem',
        margin: '0 0 0.5rem 0',
        color: '#d8e6f5',
        fontSize: '0.85rem',
        fontFamily: 'var(--font-ui, inherit)',
      }
    : {
        position: 'fixed',
        top: pos ? pos.top : -9999,
        right: pos ? pos.right : 0,
        minWidth: 240,
        background: 'rgba(20,24,32,0.97)',
        border: '1px solid #4a6a90',
        borderRadius: 6,
        padding: '0.7rem 0.8rem',
        boxShadow: '0 6px 18px rgba(0,0,0,0.6)',
        zIndex: 10002,
        color: '#d8e6f5',
        fontSize: '0.85rem',
        fontFamily: 'var(--font-ui, inherit)',
      };

  const node = (
    <div
      ref={ref}
      className={anchorClass}
      style={popoverStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1, color: '#aab8c8', marginBottom: 6 }}>
        Quick Settings
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!settings.muteAll}
          onChange={(e) => update({ muteAll: e.target.checked })}
        />
        <span>🔇 Mute all audio</span>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
        <span style={{ width: 22 }}>🎵</span>
        <input
          type="range" min="0" max="100"
          value={fmt(settings.musicVolume)}
          disabled={settings.muteAll}
          onChange={(e) => update({ musicVolume: Number(e.target.value) / 100 })}
          style={{ flex: 1 }}
        />
        <span style={{ width: 36, textAlign: 'right', color: '#aab8c8' }}>{fmt(settings.musicVolume)}%</span>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
        <span style={{ width: 22 }}>🔠</span>
        <select
          value={settings.fontScale}
          onChange={(e) => update({ fontScale: e.target.value })}
          style={{ flex: 1, background: 'rgba(60,80,110,0.4)', color: '#d8e6f5', border: '1px solid #4a6a90', borderRadius: 4, padding: '2px 4px' }}
        >
          <option value="small">Small</option>
          <option value="normal">Normal</option>
          <option value="large">Large</option>
        </select>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!settings.compactNumbers}
          onChange={(e) => update({ compactNumbers: e.target.checked })}
        />
        <span>🔢 Compact numbers (1.2k)</span>
      </label>

      <div style={{ borderTop: '1px solid rgba(74,106,144,0.4)', marginTop: 8, paddingTop: 8 }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            // Open the full panel and close the popover in the same
            // React 18 batch so both happen in one commit.
            onOpenFullSettings && onOpenFullSettings();
            onClose && onClose();
          }}
          style={{
            width: '100%', padding: '6px 8px', cursor: 'pointer',
            background: 'rgba(60,80,110,0.4)', border: '1px solid #4a6a90',
            color: '#d8e6f5', borderRadius: 4, fontSize: '0.85rem',
          }}
        >
          ⚙️ More settings…
        </button>
      </div>
    </div>
  );

  // Inline mode renders in place; desktop variant portals to body to
  // escape any sibling stacking context (game log, chat panel, etc.).
  if (inline) return node;
  if (!pos) return node; // first paint before measure: keep off-screen
  return createPortal(node, document.body);
};

export default QuickSettingsPopover;
